import React, { useMemo } from 'react';
import styles from './Pagination.module.scss';
import Icon from '../Icon/Icon';
import cx from 'classnames';
import DotsBtn from '../Menu/DotsBtn';

const Pagination = ({ currentPage, totalPages, changePage, siblingCount = 1 }) => {
  const getRange = (start, end) => {
    let length = end - start + 1;
    /*
      Create an array of certain length and set the elements within it from
      start value to end value.
    */
    return Array.from({ length }, (value, index) => index + start);
  };

  const pagesRange = useMemo(() => {
    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5;

    /*
      Case 1:
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPages]
    */
    if (totalPageNumbers >= totalPages) {
      return getRange(1, totalPages);
    }

    /*
    	Calculate left and right sibling index and make sure they are within range 1 and totalPages
    */
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    /*
      We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPages.
      Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPages - 2
    */
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    /*
    	Case 2: No left dots to show, but rights dots to be shown
    */
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = getRange(1, leftItemCount);

      return [...leftRange, <DotsBtn />, totalPages];
    }

    /*
    	Case 3: No right dots to show, but left dots to be shown
    */
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = getRange(totalPages - rightItemCount + 1, totalPages);

      return [firstPageIndex, <DotsBtn />, ...rightRange];
    }

    /*
    	Case 4: Both left and right dots to be shown
    */
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = getRange(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, <DotsBtn />, ...middleRange, <DotsBtn />, lastPageIndex];
    }
  }, [currentPage, totalPages, changePage, siblingCount]);

  return (
    <div className={styles.wrapper}>
      <div className={cx(styles.previous, { [styles.disabled]: currentPage === 1 })} onClick={() => changePage(currentPage - 1)}>
        <Icon src="chevronLeft" className={styles.icons} />
      </div>
      <div className={styles.pages}>
        {pagesRange.map((pageNumber, index) => (
          <div
            key={`${pageNumber} ${index}`}
            className={cx(styles.page, {
              [styles.currentPage]: pageNumber === currentPage,
              [styles.dots]: typeof pageNumber !== 'number',
            })}
            onClick={() => changePage(pageNumber)}
          >
            {pageNumber}
          </div>
        ))}
      </div>
      <div className={cx(styles.next, { [styles.disabled]: currentPage === totalPages })} onClick={() => changePage(currentPage + 1)}>
        <Icon src="chevronRight" className={styles.icons} />
      </div>
    </div>
  );
};

export default Pagination;
